import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { AxiosResponse } from 'axios';
import { toast } from 'react-toastify';

import ApiService from '../../services/apiService';
import { Filters } from '../../interfaces/filters';
import { Lane, LanesResponse } from '../../interfaces/services/lanes';
import { getTTCBaseUrl } from '../../utils/environmentUtils';
import { ApiStatus } from '../../utils/enums';

interface TopLanesState {
    topInboundLanesApiStatus: ApiStatus;
    topInboundLanes: Lane[];
    topOutboundLanesApiStatus: ApiStatus;
    topOutboundLanes: Lane[];
}

export const fetchTopInboundLanes = createAsyncThunk(
    'area/fetchTopInboundLanes',
    async ({
        pickup_service_area_id,
        delivery_service_area_id,
        filter_criteria,
    }: {
        pickup_service_area_id?: string | null;
        delivery_service_area_id?: string | null;
        filter_criteria: Filters;
    }) => {
        try {
            const urlParams = new URLSearchParams();
            urlParams.append('sort_by', '-freight_order_count -linehaul_revenue_usd_amount');
            urlParams.append('page_number', '1');
            urlParams.append('size', '5');

            const { data } = await ApiService.post({
                url: `${getTTCBaseUrl()}/planning/v2/insights/network/lanes?${urlParams}`,
                body: {
                    ...pickup_service_area_id && { pickup_service_area_id },
                    ...delivery_service_area_id && { delivery_service_area_id },
                    filter_criteria,
                },
            }) as AxiosResponse<LanesResponse>;

            return data;
        } catch (error) {
            console.error('Unable to load top inbound lanes', error);
            toast.error('Unable to load top inbound lanes');
            throw error;
        }
    }
);

export const fetchTopOutboundLanes = createAsyncThunk(
    'area/fetchTopOutboundLanes',
    async ({
        pickup_service_area_id,
        delivery_service_area_id,
        filter_criteria,
    }: {
        pickup_service_area_id?: string | null;
        delivery_service_area_id?: string | null;
        filter_criteria: Filters;
    }) => {
        try {
            const urlParams = new URLSearchParams();
            urlParams.append('sort_by', '-freight_order_count -linehaul_revenue_usd_amount');
            urlParams.append('page_number', '1');
            urlParams.append('size', '5');

            const { data } = await ApiService.post({
                url: `${getTTCBaseUrl()}/planning/v2/insights/network/lanes?${urlParams}`,
                body: {
                    ...pickup_service_area_id && { pickup_service_area_id },
                    ...delivery_service_area_id && { delivery_service_area_id },
                    filter_criteria,
                },
            }) as AxiosResponse<LanesResponse>;

            return data;
        } catch (error) {
            console.error('Unable to load top outbound lanes', error);
            toast.error('Unable to load top outbound lanes');
            throw error;
        }
    }
);

const initialState: TopLanesState = {
    topInboundLanesApiStatus: ApiStatus.Idle,
    topInboundLanes: [],
    topOutboundLanesApiStatus: ApiStatus.Idle,
    topOutboundLanes: [],
};

export const topLanesSlice = createSlice({
    name: 'topLanes',
    initialState,
    reducers: {},
    extraReducers(builder) {
        builder.addCase(fetchTopInboundLanes.pending, (state) => {
            state.topInboundLanesApiStatus = ApiStatus.Loading;
            state.topInboundLanes = [];
        });
        builder.addCase(fetchTopInboundLanes.fulfilled, (state, action) => {
            state.topInboundLanesApiStatus = ApiStatus.Success;
            state.topInboundLanes = action.payload.lanes;
        });
        builder.addCase(fetchTopInboundLanes.rejected, (state) => {
            state.topInboundLanesApiStatus = ApiStatus.Failure;
            state.topInboundLanes = [];
        });
        builder.addCase(fetchTopOutboundLanes.pending, (state) => {
            state.topOutboundLanesApiStatus = ApiStatus.Loading;
            state.topOutboundLanes = [];
        });
        builder.addCase(fetchTopOutboundLanes.fulfilled, (state, action) => {
            state.topOutboundLanesApiStatus = ApiStatus.Success;
            state.topOutboundLanes = action.payload.lanes;
        });
        builder.addCase(fetchTopOutboundLanes.rejected, (state) => {
            state.topOutboundLanesApiStatus = ApiStatus.Failure;
            state.topOutboundLanes = [];
        });
    },
});

export default topLanesSlice.reducer;
